import React, { useState, useEffect } from "react";
import { connect } from "react-redux";
import { useStripe, useElements, CardElement } from "@stripe/react-stripe-js";
import { toast as message } from "react-toastify";
import Swal from "sweetalert2";
import {
    actionGetPaymentMethods,
    actionSetDefaultPaymentMethod,
    actionAddPaymentMethod,
    actionRemovePaymentMethod,
    actionCreateStripeCustomer,
} from "../store/actions/subscriptionAction";

const ManagePaymentMethodsModal = ({
    show,
    onHide,
    customerId,
    user,
    actionGetPaymentMethods,
    actionSetDefaultPaymentMethod,
    actionAddPaymentMethod,
    actionRemovePaymentMethod,
    actionCreateStripeCustomer,
    paymentMethods,
    paymentMethodsLoader,
    setDefaultPaymentMethodLoader,
}) => {
    const stripe = useStripe();
    const elements = useElements();

    const [updatingPaymentMethodId, setUpdatingPaymentMethodId] = useState(null);
    const [removingId, setRemovingId] = useState(null);
    const [showAddForm, setShowAddForm] = useState(false);
    const [adding, setAdding] = useState(false);
    const [cardError, setCardError] = useState(null);
    // Track the resolved customer id (may be created on the fly if missing)
    const [localCustomerId, setLocalCustomerId] = useState(customerId);

    useEffect(() => {
        setLocalCustomerId(customerId);
    }, [customerId]);

    useEffect(() => {
        if (show && localCustomerId) {
            actionGetPaymentMethods(localCustomerId);
        }
    }, [show, localCustomerId, actionGetPaymentMethods]);

    const handleSetDefault = async (paymentMethodId) => {
        setUpdatingPaymentMethodId(paymentMethodId);
        try {
            await actionSetDefaultPaymentMethod(localCustomerId, paymentMethodId);
            await actionGetPaymentMethods(localCustomerId);
        } catch (error) {
            // handled in action
        } finally {
            setUpdatingPaymentMethodId(null);
        }
    };

    const handleRemove = async (paymentMethodId) => {
        const result = await Swal.fire({
            title: "Remove this card?",
            text: "This payment method will be removed from your account.",
            icon: "warning",
            showCancelButton: true,
            confirmButtonText: "Yes, remove it",
            cancelButtonText: "No, keep it",
            reverseButtons: true,
            confirmButtonColor: "#f46a6a",
        });
        if (!result.isConfirmed) return;
        setRemovingId(paymentMethodId);
        try {
            await actionRemovePaymentMethod(localCustomerId, paymentMethodId);
            await actionGetPaymentMethods(localCustomerId);
        } catch (error) {
            // handled in action
        } finally {
            setRemovingId(null);
        }
    };

    const handleAddCard = async () => {
        if (!stripe || !elements) {
            setCardError("Payment form is still loading. Please try again.");
            return;
        }
        setAdding(true);
        setCardError(null);
        try {
            // Ensure we have a customer to attach to
            let cid = localCustomerId;
            if (!cid) {
                const customer = await actionCreateStripeCustomer({
                    name: user?.user_fullname,
                    email: user?.user_email,
                });
                cid = customer?.id;
                if (!cid) throw new Error("Could not create a billing profile. Please try again.");
                setLocalCustomerId(cid);
            }

            const cardElement = elements.getElement(CardElement);
            const { error, paymentMethod } = await stripe.createPaymentMethod({
                type: "card",
                card: cardElement,
                billing_details: { name: user?.user_fullname, email: user?.user_email },
            });
            if (error) {
                setCardError(error.message);
                setAdding(false);
                return;
            }

            const isFirstCard = (paymentMethods?.length || 0) === 0;
            await actionAddPaymentMethod(cid, paymentMethod.id);
            if (isFirstCard) {
                await actionSetDefaultPaymentMethod(cid, paymentMethod.id);
            }
            await actionGetPaymentMethods(cid);

            cardElement.clear();
            setShowAddForm(false);
        } catch (err) {
            setCardError(err.message || "Failed to add card. Please try again.");
        } finally {
            setAdding(false);
        }
    };

    const formatCardNumber = (last4) => `•••• •••• •••• ${last4}`;

    const getCardBrandIcon = (brand) => {
        const icons = {
            visa: "fab fa-cc-visa",
            mastercard: "fab fa-cc-mastercard",
            amex: "fab fa-cc-amex",
            discover: "fab fa-cc-discover",
            diners: "fab fa-cc-diners-club",
            jcb: "fab fa-cc-jcb",
        };
        return icons[brand] || "fas fa-credit-card";
    };

    if (!show) return null;

    const busy = paymentMethodsLoader || setDefaultPaymentMethodLoader || adding;

    return (
        <div className="modal d-block" style={{ backgroundColor: "rgba(0,0,0,0.5)" }}>
            <div className="modal-dialog modal-dialog-centered modal-lg">
                <div className="modal-content">
                    <div className="modal-header">
                        <h5 className="modal-title">
                            <i className="fas fa-credit-card text-primary me-2"></i>
                            Manage Payment Methods
                        </h5>
                        <button type="button" className="btn-close" onClick={onHide} disabled={busy} />
                    </div>
                    <div className="modal-body">
                        {/* Add payment method */}
                        <div className="d-flex justify-content-end mb-3">
                            {!showAddForm && (
                                <button
                                    type="button"
                                    className="btn btn-primary btn-sm"
                                    onClick={() => { setShowAddForm(true); setCardError(null); }}
                                >
                                    <i className="fas fa-plus me-1"></i>Add Payment Method
                                </button>
                            )}
                        </div>

                        {showAddForm && (
                            <div className="card border-primary mb-3">
                                <div className="card-body">
                                    <h6 className="fw-bold mb-3">Add a card</h6>
                                    <div className="p-3 border rounded mb-2">
                                        <CardElement options={{ hidePostalCode: true }} />
                                    </div>
                                    {cardError && (
                                        <div className="text-danger small mb-2">{cardError}</div>
                                    )}
                                    <div className="d-flex justify-content-end gap-2">
                                        <button
                                            type="button"
                                            className="btn btn-light btn-sm"
                                            onClick={() => { setShowAddForm(false); setCardError(null); }}
                                            disabled={adding}
                                        >
                                            Cancel
                                        </button>
                                        <button
                                            type="button"
                                            className="btn btn-primary btn-sm"
                                            onClick={handleAddCard}
                                            disabled={adding || !stripe}
                                        >
                                            {adding ? (
                                                <><span className="spinner-border spinner-border-sm me-1" />Saving…</>
                                            ) : (
                                                <>Save card</>
                                            )}
                                        </button>
                                    </div>
                                </div>
                            </div>
                        )}

                        {paymentMethodsLoader && (
                            <div className="row g-3">
                                {[1, 2].map((i) => (
                                    <div key={i} className="col-12">
                                        <div className="card border-secondary">
                                            <div className="card-body p-3">
                                                <div className="d-flex align-items-center justify-content-between">
                                                    <div className="d-flex align-items-center">
                                                        <div className="skeleton me-3" style={{ width: 48, height: 32, borderRadius: 4 }} />
                                                        <div className="flex-grow-1">
                                                            <div className="skeleton mb-2" style={{ height: 20, width: 180 }} />
                                                            <div className="skeleton" style={{ height: 16, width: 120 }} />
                                                        </div>
                                                    </div>
                                                    <div className="skeleton" style={{ height: 32, width: 120, borderRadius: 4 }} />
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}

                        {!paymentMethodsLoader && paymentMethods && paymentMethods.length === 0 && !showAddForm && (
                            <div className="text-center py-5">
                                <i className="fas fa-credit-card text-muted fs-1 mb-3"></i>
                                <h6 className="text-muted">No Payment Methods Found</h6>
                                <p className="text-muted small">Add a card to manage your subscription billing.</p>
                            </div>
                        )}

                        {!paymentMethodsLoader && paymentMethods && paymentMethods.length > 0 && (
                            <div className="row g-3">
                                {paymentMethods.map((method) => (
                                    <div key={method.id} className="col-12">
                                        <div className={`card h-100 ${method.is_default ? "border-success" : "border-secondary"}`}>
                                            <div className="card-body p-3">
                                                <div className="d-flex align-items-center justify-content-between">
                                                    <div className="d-flex align-items-center">
                                                        <i
                                                            className={`${getCardBrandIcon(method.brand)} fs-2 me-3`}
                                                            style={{
                                                                color: method.brand === "visa" ? "#1a1f71" :
                                                                    method.brand === "mastercard" ? "#eb001b" :
                                                                        method.brand === "amex" ? "#006fcf" : "#6c757d",
                                                            }}
                                                        />
                                                        <div>
                                                            <h6 className="mb-1 fw-bold text-capitalize">
                                                                {method.brand || "Card"}
                                                                {method.last4 && ` ${formatCardNumber(method.last4)}`}
                                                            </h6>
                                                            <p className="mb-1 small text-muted">
                                                                Expires: {method.exp_month || "XX"}/{method.exp_year || "XXXX"}
                                                            </p>
                                                            {method.is_default && (
                                                                <span className="badge bg-success small">
                                                                    <i className="fas fa-star me-1"></i>Default
                                                                </span>
                                                            )}
                                                        </div>
                                                    </div>
                                                    <div className="d-flex align-items-center gap-2">
                                                        {method.is_default ? (
                                                            <button type="button" className="btn btn-success btn-sm" disabled>
                                                                <i className="fas fa-star me-1"></i>Default
                                                            </button>
                                                        ) : (
                                                            <>
                                                                <button
                                                                    type="button"
                                                                    className="btn btn-outline-primary btn-sm"
                                                                    onClick={() => handleSetDefault(method.id)}
                                                                    disabled={updatingPaymentMethodId === method.id}
                                                                >
                                                                    {updatingPaymentMethodId === method.id ? (
                                                                        <><span className="spinner-border spinner-border-sm me-1" />Setting…</>
                                                                    ) : (
                                                                        <><i className="far fa-star me-1"></i>Set as Default</>
                                                                    )}
                                                                </button>
                                                                <button
                                                                    type="button"
                                                                    className="btn btn-outline-danger btn-sm"
                                                                    onClick={() => handleRemove(method.id)}
                                                                    disabled={removingId === method.id}
                                                                    title="Remove this card"
                                                                >
                                                                    {removingId === method.id ? (
                                                                        <span className="spinner-border spinner-border-sm" />
                                                                    ) : (
                                                                        <i className="fas fa-trash" />
                                                                    )}
                                                                </button>
                                                            </>
                                                        )}
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}

                        <div className="mt-4 p-3 bg-light rounded">
                            <h6 className="fw-bold mb-2">
                                <i className="fas fa-info-circle text-info me-2"></i>
                                Payment Method Information
                            </h6>
                            <ul className="mb-0 small text-muted">
                                <li>Your default payment method is used for all subscription charges</li>
                                <li>Add or remove cards anytime; set any card as default</li>
                                <li>To remove the default card, set another card as default first</li>
                                <li>All payment information is securely stored by Stripe</li>
                            </ul>
                        </div>
                    </div>
                    <div className="modal-footer">
                        <button type="button" className="btn btn-secondary" onClick={onHide} disabled={busy}>
                            Close
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

const mapStateToProps = (state) => ({
    user: state.auth?.user,
    paymentMethods: state.subscription?.paymentMethods || [],
    paymentMethodsLoader: state.subscription?.paymentMethodsLoader || false,
    setDefaultPaymentMethodLoader: state.subscription?.setDefaultPaymentMethodLoader || false,
});

export default connect(mapStateToProps, {
    actionGetPaymentMethods,
    actionSetDefaultPaymentMethod,
    actionAddPaymentMethod,
    actionRemovePaymentMethod,
    actionCreateStripeCustomer,
})(ManagePaymentMethodsModal);
